/**
 * CapacityBulletBar — Stephen Few's bullet graph for "occupancy vs licensed capacity".
 *
 * Plots weighted (or raw) occupancy as a horizontal measure bar against a licensed-capacity
 * TICK on a FIXED 0–120% scale, so an over-capacity value visibly CROSSES the tick — a
 * position signal that survives without perceiving hue (deuteranopia-safe, WCAG 1.4.1). Behind
 * the fill sit the org's warning/critical threshold zones, tinted with the shared capacity fill
 * tokens and delimited by a 3:1-compliant boundary (the -on-surface border role) so each zone
 * is a perceptible graphical object (WCAG 1.4.11 non-text contrast).
 *
 * Shared by CalendarCell, GroupCard and the Dashboard (decision D7) so the visual language is
 * identical everywhere. All colours route through capacityColor.ts — no new hex. Positions and
 * widths are data-driven, so they use inline geometry exactly as ui/progress.tsx does for its
 * fill transform; this is the repo's sanctioned pattern for a value-driven bar, not static
 * styling. The numeric value is announced by the surrounding cell, so the bar itself is
 * aria-hidden to avoid double-announcing.
 */

import { cn } from '@/lib/utils';
import {
  getCapacityColor,
  getCapacityLevel,
  type CapacityThresholds,
} from '@/lib/capacityColor';

/** The fixed upper bound of the bar's scale, in utilisation percent. */
const SCALE_MAX = 120;
/** Licensed capacity (100%) as a fraction of the 0–120 scale — the tick position. */
const CAPACITY_TICK_PERCENT = (100 / SCALE_MAX) * 100; // 83.333…

interface CapacityBulletBarProps {
  /** The occupancy to plot, in utilisation percent (may exceed 100). Drives the fill LENGTH. */
  value: number;
  /** Org-configured warning/critical thresholds, in utilisation percent. */
  thresholds: CapacityThresholds;
  /**
   * The percent that drives the fill COLOUR/level, when it must differ from `value`. Callers
   * that toggle a metric (headcount vs weighted) pass the max of the two here so flipping the
   * toggle changes only the bar's length, never its colour (C6). Defaults to `value`.
   */
  colorPercent?: number;
  /** When the metric is 0, render the quiet empty style instead of a normal fill. */
  treatZeroAsEmpty?: boolean;
  /** Track height. Defaults to the compact cell size. */
  className?: string;
}

/** Clamp a utilisation percent to [0, SCALE_MAX] and return its position on the track, in %. */
function toTrackPercent(utilisationPercent: number): number {
  const clamped = Math.max(0, Math.min(utilisationPercent, SCALE_MAX));
  return (clamped / SCALE_MAX) * 100;
}

export function CapacityBulletBar({
  value,
  thresholds,
  colorPercent,
  treatZeroAsEmpty = false,
  className,
}: CapacityBulletBarProps) {
  // Colour follows colorPercent when supplied (so a metric toggle never recolours the bar),
  // otherwise the plotted value.
  const levelPercent = colorPercent ?? value;
  const level = getCapacityLevel(levelPercent, thresholds, { treatZeroAsEmpty });
  const colors = getCapacityColor(levelPercent, thresholds, { treatZeroAsEmpty });

  // Zone start positions on the 0–120 track. A threshold at/above the scale max collapses to a
  // zero-width zone that is simply not rendered.
  const warningStart = toTrackPercent(thresholds.warningThreshold);
  const criticalStart = toTrackPercent(thresholds.criticalThreshold);
  const fillPercent = toTrackPercent(value);

  // The fill carries the traffic light: green below the warning threshold, amber above it, red
  // above the critical one — the same three colours the Settings "Capacity Badge Preview" shows.
  // Only `empty` (no occupancy at all) recedes into a neutral baseline; it has no status.
  const fillClass = level === 'empty' ? 'bg-muted-foreground/40' : colors.bar;

  return (
    <div
      className={cn('relative w-full overflow-hidden rounded-full bg-muted', className ?? 'h-1.5')}
      aria-hidden="true"
      data-testid="capacity-bullet-bar"
    >
      {/* Warning zone: from the warning threshold up to the critical threshold. Tinted with the
          warning fill token; its left edge is a 3:1-compliant boundary (the -on-surface border
          role) marking where the warning threshold sits. */}
      {warningStart < 100 && (
        <div
          className="absolute inset-y-0 border-l bg-capacity-warning/20 border-capacity-warning-on-surface"
          style={{ left: `${warningStart}%`, right: `${100 - Math.min(criticalStart, 100)}%` }}
          data-testid="capacity-band-warning"
          data-band-start={warningStart}
        />
      )}
      {/* Critical zone: from the critical threshold to the top of the scale. */}
      {criticalStart < 100 && (
        <div
          className="absolute inset-y-0 right-0 border-l bg-capacity-critical/25 border-capacity-critical-on-surface"
          style={{ left: `${criticalStart}%` }}
          data-testid="capacity-band-critical"
          data-band-start={criticalStart}
        />
      )}

      {/* Measure fill — the occupancy, drawn on top of the zones. */}
      <div
        className={cn('absolute inset-y-0 left-0 rounded-full', fillClass)}
        style={{ width: `${fillPercent}%` }}
        data-testid="capacity-bullet-fill"
        data-fill-percent={fillPercent}
      />

      {/* Licensed-capacity tick at a fixed position on the 0–120 scale, so an over-capacity
          fill visibly crosses it. */}
      <div
        className="absolute inset-y-0 w-px bg-foreground/50"
        style={{ left: `${CAPACITY_TICK_PERCENT}%` }}
        data-testid="capacity-tick"
        data-tick-percent={CAPACITY_TICK_PERCENT}
      />
    </div>
  );
}
